import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { axiosInstance } from "../../utils/axios";
import { PlusIcon, ArrowPathIcon } from "@heroicons/react/20/solid";
import AdminSidebar from "../../components/AdminSidebar";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import LoadingComponent from "../../components/LoadingComponent";

const TONER_COLORS = ["Black", "Cyan", "Magenta", "Yellow"];

export default function TonerForm() {
  // Hooks and Utilities
  const queryClient = useQueryClient();
  const { objID } = useParams();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      part_number: "",
      toner_model: "",
      machine_model: "",
      color: "",
      price: "",
      toner_price: "",
      weight: "",
      units: 0,
      device_brand_id: null,
    },
  });

  const device_brand_id = watch("device_brand_id");

  // Brands for the "Select Brand" chips
  const {
    data: brandsData,
    isLoading: isLoadingBrands,
    error: brandsError,
  } = useQuery({
    queryKey: ["device-brands"],
    queryFn: async () => {
      const response = await axiosInstance.get(
        window.REACT_APP_API_ENDPOINT + "/device-brands?type=all"
      );
      return response.data.data;
    },
  });

  // Existing toner models for the selected brand (autocomplete suggestions)
  const { data: tonerModelOptions = [] } = useQuery({
    queryKey: ["toner-models", device_brand_id],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/toners/brand/${device_brand_id}/models`
      );
      return response.data.data;
    },
    enabled: !!device_brand_id,
  });

  const {
    data: tonerData,
    isLoading: isLoadingToner,
    error: tonerError,
  } = useQuery({
    queryKey: ["toner", objID],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/toners/${objID}`
      );
      return response.data.data;
    },
    enabled: !!objID,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Effects
  useEffect(() => {
    if (tonerData && objID) {
      reset({
        part_number: tonerData.part_number || "",
        toner_model: tonerData.toner_model || "",
        machine_model: tonerData.machine_model || "",
        color: tonerData.color || "",
        price: tonerData.price ?? "",
        toner_price: tonerData.toner_price ?? "",
        weight: tonerData.weight ?? "",
        units: tonerData.units ?? 0,
        device_brand_id: tonerData.device_brand_id || null,
      });
    }
  }, [tonerData, objID, reset]);

  // Mutations
  const saveTonerMutation = useMutation({
    mutationFn: async (tonerFields) => {
      if (objID) {
        return axiosInstance.put(
          `${window.REACT_APP_API_ENDPOINT}/toners/${objID}`,
          tonerFields
        );
      }
      return axiosInstance.post(
        window.REACT_APP_API_ENDPOINT + "/toners",
        tonerFields
      );
    },
    onSuccess: () => {
      toast.success(
        objID ? "Toner updated successfully" : "Toner created successfully"
      );
      queryClient.invalidateQueries(["toners"]);
      navigate("/admin/toner-stock");
    },
    onError: (error) => {
      const serverErrors = error?.response?.data?.errors;
      if (serverErrors) {
        Object.entries(serverErrors).forEach(([field, messages]) => {
          setError(field, { type: "server", message: messages[0] });
        });
      }
      toast.error(
        error?.response?.data?.message ||
          `Failed to ${objID ? "update" : "create"} toner. Please try again.`
      );
    },
  });

  // Handlers
  const handleBrandSelect = (brand) => {
    setValue("device_brand_id", brand.id);
    clearErrors("device_brand_id");
  };

  const onSubmit = handleSubmit((data) => {
    if (!data.device_brand_id) {
      setError("device_brand_id", {
        type: "manual",
        message: "Please select a brand",
      });
      return;
    }

    saveTonerMutation.mutate({
      part_number: data.part_number,
      toner_model: data.toner_model,
      machine_model: data.machine_model,
      color: data.color,
      price: parseFloat(data.price),
      toner_price:
        data.toner_price !== "" && data.toner_price !== null
          ? parseFloat(data.toner_price)
          : null,
      weight: parseFloat(data.weight),
      units: parseInt(data.units) || 0,
      device_brand_id: data.device_brand_id,
    });
  });

  // Conditional Rendering
  if (isLoadingToner && objID) {
    return (
      <AdminSidebar>
        <LoadingComponent />
      </AdminSidebar>
    );
  }

  if (tonerError && objID) {
    return (
      <AdminSidebar>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          Failed to fetch toner data. Please try again.
        </div>
      </AdminSidebar>
    );
  }

  const inputClassName = (hasFieldError) =>
    `w-full rounded-md border-0 bg-white py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
      hasFieldError ? "ring-red-500" : "ring-gray-300"
    } focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`;

  return (
    <AdminSidebar>
      <div className="bg-white pb-8">
        <div className="bg-lightBlue px-4 sm:px-6 lg:px-8 py-4">
          <h2 className="text-textBlue text-lg flex font-bold items-center">
            <span className="mr-2">
              {objID ? "Edit Toner" : "Register a Toner"}
            </span>{" "}
            <PlusIcon className="w-6" />
          </h2>
        </div>
        <div className="mt-8 flow-root px-4 sm:px-6 lg:px-8">
          <div className="inline-block min-w-full py-2 align-middle">
            <form onSubmit={onSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {/* Part Number */}
                <div>
                  <h6 className="text-sm font-bold mb-1">Part Number</h6>
                  <input
                    type="text"
                    placeholder="Enter part number"
                    {...register("part_number", {
                      required: "Part number is required",
                    })}
                    className={inputClassName(errors.part_number)}
                  />
                  {errors.part_number && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.part_number.message}
                    </p>
                  )}
                </div>

                {/* Color */}
                <div>
                  <h6 className="text-sm font-bold mb-1">Color</h6>
                  <select
                    {...register("color", {
                      required: "Please select a color",
                    })}
                    className={inputClassName(errors.color)}
                  >
                    <option value="">Select Color</option>
                    {TONER_COLORS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  {errors.color && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.color.message}
                    </p>
                  )}
                </div>

                {/* Machine Model */}
                <div>
                  <h6 className="text-sm font-bold mb-1">Machine Model</h6>
                  <input
                    type="text"
                    placeholder="Enter machine model"
                    {...register("machine_model", {
                      required: "Machine model is required",
                    })}
                    className={inputClassName(errors.machine_model)}
                  />
                  {errors.machine_model && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.machine_model.message}
                    </p>
                  )}
                </div>

                {/* Toner Model — autocomplete of existing toner models for the brand */}
                <div>
                  <h6 className="text-sm font-bold mb-1">Toner Model</h6>
                  <input
                    type="text"
                    list="toner-model-options"
                    placeholder="Enter toner model"
                    {...register("toner_model", {
                      required: "Toner model is required",
                    })}
                    className={inputClassName(errors.toner_model)}
                  />
                  <datalist id="toner-model-options">
                    {tonerModelOptions.map((m) => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                  {device_brand_id && tonerModelOptions.length > 0 && (
                    <p className="text-gray-400 text-xs mt-1">
                      Pick an existing toner model for this brand or type a new
                      one.
                    </p>
                  )}
                  {errors.toner_model && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.toner_model.message}
                    </p>
                  )}
                </div>

                {/* Price */}
                <div>
                  <h6 className="text-sm font-bold mb-1">Price</h6>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter price"
                    {...register("price", {
                      required: "Price is required",
                      min: { value: 0, message: "Price must be 0 or greater" },
                    })}
                    className={inputClassName(errors.price)}
                  />
                  {errors.price && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.price.message}
                    </p>
                  )}
                </div>

                {/* Toner Price — reference price (optional) */}
                <div>
                  <h6 className="text-sm font-bold mb-1">Toner Price</h6>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter toner price (optional)"
                    {...register("toner_price", {
                      min: {
                        value: 0,
                        message: "Toner price must be 0 or greater",
                      },
                    })}
                    className={inputClassName(errors.toner_price)}
                  />
                  {errors.toner_price && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.toner_price.message}
                    </p>
                  )}
                </div>

                {/* Weight */}
                <div>
                  <h6 className="text-sm font-bold mb-1">Weight</h6>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter weight"
                    {...register("weight", {
                      required: "Weight is required",
                      min: { value: 0, message: "Weight must be 0 or greater" },
                    })}
                    className={inputClassName(errors.weight)}
                  />
                  {errors.weight && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.weight.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Units */}
              <div className="mb-6 max-w-xs">
                <h6 className="text-sm font-bold mb-1">Units</h6>
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  {...register("units", { min: 0 })}
                  className={inputClassName(false)}
                />
              </div>

              {/* Select Brand */}
              <h4 className="mb-4 font-bold">Select Brand</h4>
              <div className="flex flex-wrap">
                {isLoadingBrands ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">
                      Loading brands...
                    </span>
                  </div>
                ) : brandsError ? (
                  <div className="text-red-500">Failed to load brands</div>
                ) : (
                  brandsData?.map((brand) => (
                    <div
                      key={brand.id}
                      className={`flex items-center px-4 border border-textBlue rounded dark:border-textBlue h-8 mr-2 mb-2 cursor-pointer ${
                        device_brand_id === brand.id
                          ? "bg-blue-200"
                          : "bg-white"
                      }`}
                      onClick={() => handleBrandSelect(brand)}
                    >
                      <div
                        className={`w-4 h-4 border border-textBlue rounded-full mr-2 ${
                          device_brand_id === brand.id
                            ? "bg-blue-600"
                            : "bg-white"
                        }`}
                      />
                      {brand.name}
                    </div>
                  ))
                )}
              </div>
              <p className="text-red-500 text-xs mb-6 pt-2">
                {errors?.device_brand_id?.message}
              </p>

              <button
                type="submit"
                disabled={saveTonerMutation.isPending}
                className="w-full rounded-md bg-bluePrimary h-11 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-bluePrimary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                {saveTonerMutation.isPending ? (
                  <div className="flex items-center justify-center gap-2">
                    <ArrowPathIcon className="w-5 h-5 animate-spin" />
                    Saving...
                  </div>
                ) : (
                  "Save Toner"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}
